/**
 * Sanitizadores de entrada reutilizables para formularios.
 *
 * Centralizan las reglas de "qué caracteres se permiten escribir" para que todos
 * los campos del proyecto se comporten igual. Aplícalos en el `onChange` del input
 * (filtrado en vivo) y/o en el `transform` de Zod (defensa al enviar).
 */

/**
 * Teléfono en formato Perú: solo dígitos, con un `+` opcional al inicio para el
 * código de país (+51). Elimina espacios, guiones, paréntesis y cualquier letra.
 *
 * Ejemplos:
 *   "+51 987 654 321" -> "+51987654321"
 *   "(01) 234-5678"   -> "012345678"
 *   "abc987"          -> "987"
 */
export function sanitizePhone(raw: string): string {
  if (!raw) return ''
  const hasLeadingPlus = raw.trimStart().startsWith('+')
  const digits = raw.replace(/\D/g, '')
  return (hasLeadingPlus ? '+' : '') + digits
}

import type React from 'react'
import type { UseFormRegisterReturn } from 'react-hook-form'

/**
 * Envuelve el resultado de `register('campo')` de react-hook-form para que el
 * input de teléfono filtre en vivo a formato Perú y use el teclado numérico.
 * Uso: `<Input {...phoneRegister(register('mainPhone'))} maxLength={...} />`
 */
export function phoneRegister<T extends UseFormRegisterReturn>(registration: T) {
  return {
    ...registration,
    inputMode: 'tel' as const,
    autoComplete: 'tel' as const,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = sanitizePhone(e.target.value)
      return registration.onChange(e)
    },
  }
}

/**
 * Trunca un nombre de archivo a `max` caracteres preservando la extensión.
 * Evita que nombres exageradamente largos rompan columnas o descuadren la UI.
 *
 * Ejemplo (max=10): "documento-larguisimo.pdf" -> "docume.pdf"
 */
export function truncateFileName(name: string, max = 255): string {
  if (!name || name.length <= max) return name
  const dot = name.lastIndexOf('.')
  // Sin extensión (o el punto es el primer carácter): recorte simple.
  if (dot <= 0) return name.slice(0, max)
  const ext = name.slice(dot) // incluye el punto
  // Si la extensión por sí sola ya excede el límite, recorta todo el string.
  if (ext.length >= max) return name.slice(0, max)
  const baseMax = max - ext.length
  return name.slice(0, baseMax) + ext
}
